// useRiskDashboard — loads all Risk Portal dashboard data from the SAME backend
// endpoints the legacy RiskPortal uses, and maps each response into the shape
// the RiskPortal1 widgets consume. No static data: every number here comes from
// the API. Scope (business unit / project) is passed straight through to the
// dashboard endpoint, exactly like RiskPortal's loadDashboard.
//
//   const { loading, totalRisks, severity, byState, byCategory, treatment,
//           treatmentTotal, trend, buRows, buOptions, projectOptions }
//       = useRiskDashboard({ buId, projectId });

import { useCallback, useEffect, useState } from 'react';
import {
    risk_portal_dashboard,
    risk_portal_portfolio_trend,
    risk_portal_bu_breakdown,
    risk_portal_bus_list,
    risk_portal_projects_list,
    risk_portal_project_breakdown,
    risk_portal_risks,
    risk_portal_categories,
    risk_portal_heatmap,
    total_risk_created,
} from '../../services';

// Same envelope-unwrap helper RiskPortal uses.
const extract = (res) =>
    res?.data?.response_data?.data || res?.data?.data ||
    res?.statusCode?.response_data?.data || res?.statusCode?.data || null;

const titleCase = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Normalise a BU/org counts object into the table row shape.
const toCounts = (c = {}) => ({
    total: c.total || 0, critical: c.critical || 0, high: c.high || 0,
    medium: c.medium || 0, low: c.low || 0, open: c.open || 0,
});

// Convert the main dashboard's likelihood_impact_matrix (a list/obj of
// { impact, likelihood, count }) into the 5×5 grid the RiskHeatmap component
// reads: matrix[likelihood-1][impact-1]. Both axes are 1..5 (impact 5 =
// Catastrophic, likelihood 5 = Almost Certain). Returns null if unusable, so the
// caller can fall back to the portal heatmap endpoint.
const toHeatmapGrid = (raw) => {
    if (!raw) return null;
    const arr = Array.isArray(raw) ? raw : Object.values(raw);
    if (!arr.length) return null;
    const grid = Array.from({ length: 5 }, () => [0, 0, 0, 0, 0]);
    arr.forEach((it) => {
        const impact = Number(it?.impact ?? it?.Impact ?? 0);
        const likelihood = Number(it?.likelihood ?? it?.Likelihood ?? 0);
        const count = Number(it?.count ?? (Array.isArray(it?.risks) ? it.risks.length : 0));
        if (impact >= 1 && impact <= 5 && likelihood >= 1 && likelihood <= 5) {
            grid[likelihood - 1][impact - 1] = count;
        }
    });
    return grid;
};

// ISO 31000 5x5 rating from a score — used to backfill a risk's rating when the
// API didn't return one (matches the legacy RiskPortal's ratingOf5x5).
const ratingOf = (score) => (score >= 16 ? 'Critical' : score >= 10 ? 'High' : score >= 5 ? 'Medium' : 'Low');

// Normalise a top-risk item (from the dashboard's top_risks OR the risks list —
// both share these field names) into the TopRisksTable row shape.
const toTopRisk = (r = {}) => {
    const score = r.inherent_score ?? (r.likelihood && r.impact ? r.likelihood * r.impact : 0);
    return {
        risk_id: r.risk_id,
        id: r.id ?? r.risk_registry_id ?? r.pk,
        risk: r.risk || '',
        score,
        rating: r.inherent_rating || ratingOf(score),
        state: r.state_label || r.state || '',
        owner: r.risk_owner || '',
    };
};

// A single "my assigned risk" row for the Risk Owner dashboard. Keeps the raw
// state + label separate (StatusBadge maps the canonical state; the label is the
// org's display override) and carries the target date the owner table shows.
const toMyRisk = (r = {}) => {
    const score = r.inherent_score ?? (r.likelihood && r.impact ? r.likelihood * r.impact : 0);
    return {
        risk_id: r.risk_id,
        id: r.id ?? r.risk_registry_id ?? r.pk,
        risk: r.risk || '',
        score,
        rating: r.inherent_rating || ratingOf(score),
        state: r.state || '',
        stateLabel: r.state_label || '',
        target: r.target_date || '',
    };
};

const EMPTY = {
    totalRisks: 0,
    severity: { critical: 0, high: 0, medium: 0, low: 0 },
    byState: [],
    byCategory: [],
    treatment: [],
    treatmentTotal: 0,
    trend: [],
    buRows: [],
    projectRows: [],
    topRisks: [],
    // Risk Owner dashboard ("My Risk Tasks") — risks assigned to the caller.
    myRisks: [],
    myRisksCount: 0,
    overdueActions: 0,
    // Risk Manager dashboard — 5x5 inherent likelihood×impact heatmap.
    heatmap: { matrix: null, totalAssessed: 0 },
    buOptions: [{ value: 'all', label: 'All Business Units' }],
    projectOptions: [{ value: 'all', label: 'All Projects' }],
    // Raw lists — for the Add/Edit Risk form's Category / BU / Project pickers.
    categories: [],
    businessUnits: [],
    projects: [],
};

export function useRiskDashboard({ buId, projectId } = {}) {
    const [data, setData] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    // Bump to force a refetch (e.g. after a delete) without changing scope.
    const [nonce, setNonce] = useState(0);
    const reload = useCallback(() => setNonce((n) => n + 1), []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        const params = {};
        if (buId && buId !== 'all') params.bu_id = buId;
        if (projectId && projectId !== 'all') params.project_id = projectId;

        // allSettled so one failing endpoint doesn't blank the whole dashboard.
        Promise.allSettled([
            risk_portal_dashboard(Object.keys(params).length ? params : undefined),
            risk_portal_portfolio_trend({ months: 6 }),
            risk_portal_bu_breakdown({ rollup: 'true' }),
            risk_portal_bus_list({ include_inactive: 'true' }),
            risk_portal_projects_list(),
            risk_portal_project_breakdown(),
            risk_portal_risks(Object.keys(params).length ? { page: 1, ...params } : { page: 1 }),
            risk_portal_categories(),
            risk_portal_heatmap({ type: 'inherent' }),
            // Severity KPIs come from the SAME endpoint the main-tool Risk
            // Registry widget uses (scanner/risk-registry/stats/ → by_severity),
            // so both dashboards show identical severity counts. The portal's own
            // by_inherent_rating only counts scored risks, which diverged.
            total_risk_created(Object.keys(params).length ? params : undefined),
        ]).then((results) => {
            if (cancelled) return;
            const [dash, trend, bu, bus, proj, projBd, risksList, cats, hm, stats] = results.map((r) => (r.status === 'fulfilled' ? extract(r.value) : null));

            const d = dash || {};
            // All-risks severity (matches the main dashboard). Falls back to the
            // dashboard endpoint's inherent rating if the stats call fails.
            const bySeverity = (stats && stats.by_severity) || {};
            const inherent = d.by_inherent_rating || {};
            // Heatmap from the SAME source as the main dashboard's RiskGraph
            // (likelihood_impact_matrix), so the two matrices read identically.
            const limGrid = toHeatmapGrid(stats && stats.likelihood_impact_matrix);
            const treatment = Object.entries(d.treatment_by_status || {}).map(([k, v]) => ({ label: titleCase(k), value: v }));

            const orgCounts = bu?.organization_level?.counts || {};
            const buList = bu?.business_units || [];
            const buRows = [];
            if ((orgCounts.total || 0) > 0) {
                buRows.push({ name: 'Organization-level (no BU)', muted: true, ...toCounts(orgCounts) });
            }
            [...buList]
                .sort((a, b) => (b.counts_with_descendants?.total || 0) - (a.counts_with_descendants?.total || 0))
                .slice(0, 8)
                .forEach((b) => buRows.push({ name: b.name, code: b.code, ...toCounts(b.counts_with_descendants || {}) }));

            // Project distribution — unassigned ("Not project-bound") first, then
            // the top 8 projects by total, mirroring the BU breakdown shape.
            const projUnassigned = projBd?.unassigned?.counts || {};
            const projList = projBd?.projects || [];
            const projectRows = [];
            if ((projUnassigned.total || 0) > 0) {
                projectRows.push({ name: 'Not project-bound', muted: true, ...toCounts(projUnassigned) });
            }
            [...projList]
                .sort((a, b) => (b.counts?.total || 0) - (a.counts?.total || 0))
                .slice(0, 8)
                .forEach((p) => projectRows.push({ name: p.name, code: p.code, ...toCounts(p.counts || {}) }));

            // Top risks by score — prefer the dashboard's own top_risks, else
            // backfill from the risks list (same fallback the legacy portal uses).
            const rawTop = (Array.isArray(d.top_risks) && d.top_risks.length)
                ? d.top_risks
                : ((risksList && Array.isArray(risksList.results)) ? risksList.results : []);
            // The dashboard's `top_risks` payload often omits the numeric DB id
            // (it only carries risk_id/score/etc.), which Edit/Delete need for
            // get_by_id. Backfill it from the risks list, matched by risk_id.
            const idByRiskId = new Map(
                ((risksList && risksList.results) || [])
                    .filter((r) => r && r.risk_id != null)
                    .map((r) => [r.risk_id, r.id])
            );
            const topRisks = [...rawTop]
                .map(toTopRisk)
                .map((r) => ({ ...r, id: r.id ?? idByRiskId.get(r.risk_id) }))
                .sort((a, b) => (b.score || 0) - (a.score || 0))
                .slice(0, 5);

            const buOptions = [
                { value: 'all', label: 'All Business Units' },
                ...(bus?.business_units || [])
                    .filter((b) => b.is_active !== false)
                    .map((b) => ({ value: String(b.id), label: b.code ? `${b.name} (${b.code})` : b.name })),
            ];
            const projectOptions = [
                { value: 'all', label: 'All Projects' },
                ...(proj?.projects || [])
                    .filter((p) => p.is_active !== false)
                    .map((p) => ({ value: String(p.id), label: p.code ? `${p.name} (${p.code})` : p.name })),
            ];

            setData({
                totalRisks: d.total_risks || 0,
                severity: {
                    critical: bySeverity.critical ?? inherent.Critical ?? 0,
                    high: bySeverity.high ?? inherent.High ?? 0,
                    medium: bySeverity.medium ?? inherent.Medium ?? 0,
                    low: bySeverity.low ?? inherent.Low ?? 0,
                },
                byState: Object.entries(d.by_state || {}).map(([k, v]) => ({ label: titleCase(k), value: v })),
                byCategory: (d.by_type || []).map((t) => ({ label: titleCase(t.risk_type || 'Unknown'), value: t.count })),
                treatment,
                treatmentTotal: treatment.reduce((a, t) => a + t.value, 0),
                trend: (trend?.series || []).map((s) => ({
                    month: s.period_label, inflow: s.inflow || 0, outflow: s.outflow || 0,
                    open: s.open_at_end || 0, net: s.net || 0,
                })),
                buRows,
                projectRows,
                topRisks,
                myRisks: (d.my_risks || []).map(toMyRisk),
                myRisksCount: d.my_risks_count ?? (d.my_risks || []).length,
                overdueActions: d.overdue_actions || 0,
                heatmap: { matrix: limGrid || hm?.matrix || null, totalAssessed: hm?.total_assessed || 0 },
                buOptions,
                projectOptions,
                categories: Array.isArray(cats) ? cats : (cats?.categories || []),
                businessUnits: bus?.business_units || [],
                projects: proj?.projects || [],
            });
            setLoading(false);
        });

        return () => { cancelled = true; };
    }, [buId, projectId, nonce]);

    return { ...data, loading, reload };
}
